import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiPlus, FiEdit2, FiAlertTriangle } from 'react-icons/fi';
import { fetchUsers, createUser, updateUser } from '../../utils/sentimentApi';

const ROLES = ['admin', 'team_lead', 'support_agent', 'sales', 'marketing', 'finance'];
const STATUSES = ['active', 'inactive'];

const EMPTY_FORM = { email: '', name: '', role: 'support_agent', password: '', status: 'active' };

export default function SentimentUsers({ showToast }) {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  const [modal, setModal]         = useState(null); // null | 'create' | user object (edit)
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchUsers();
      setUsers(Array.isArray(data) ? data : (data.items || []));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setModal('create');
  };

  const openEdit = (user) => {
    setForm({ email: user.email, name: user.name, role: user.role, password: '', status: user.status });
    setModal(user);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (modal === 'create') {
        const { password, ...rest } = form;
        const body = { ...rest, password };
        const created = await createUser(body);
        setUsers(prev => [created, ...prev]);
        showToast?.(`User ${created.email} created`, 'success');
      } else {
        const body = { name: form.name, role: form.role, status: form.status };
        const updated = await updateUser(modal.user_id, body);
        setUsers(prev => prev.map(u => u.user_id === modal.user_id ? updated : u));
        showToast?.(`User ${updated.email} updated`, 'success');
      }
      setModal(null);
    } catch (e) {
      setError(e.message);
      showToast?.(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

  return (
    <div>
      <div className="sm-section-header">
        <h2 className="sm-section-title">Users ({users.length})</h2>
        <div className="sm-filters">
          <button className="sm-btn sm-btn-ghost" onClick={load} title="Refresh"><FiRefreshCw size={14} /></button>
          <button className="sm-btn sm-btn-primary" onClick={openCreate} style={{ gap: 6 }}>
            <FiPlus size={14} /> New User
          </button>
        </div>
      </div>

      {error && <div className="sm-alert-err"><FiAlertTriangle /> {error}</div>}

      {loading ? (
        <div className="sm-loading"><div className="sm-spinner sm-spinner-lg" /><span>Loading users…</span></div>
      ) : users.length === 0 ? (
        <div className="sm-empty"><div className="sm-empty-icon">👥</div><span>No users found.</span></div>
      ) : (
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.user_id} style={{ '--row-delay': `${i * 25}ms` }}>
                  <td style={{ fontWeight: 600 }}>{u.name}</td>
                  <td style={{ fontSize: 13 }}>{u.email}</td>
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--sm-primary)' }}>
                      {u.role?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td>
                    <span className={`sm-badge ${u.status === 'active' ? 'sm-badge-resolved' : 'sm-badge-system'}`}>
                      {u.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--sm-muted)' }}>{fmtDate(u.last_login)}</td>
                  <td style={{ fontSize: 12, color: 'var(--sm-muted)' }}>{fmtDate(u.created_at)}</td>
                  <td>
                    <button
                      className="sm-btn sm-btn-ghost"
                      style={{ padding: '3px 8px', fontSize: 11 }}
                      onClick={() => openEdit(u)}
                    >
                      <FiEdit2 size={12} /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="sm-modal-overlay" onClick={() => setModal(null)}>
          <div className="sm-modal" onClick={e => e.stopPropagation()}>
            <h3 className="sm-modal-title">{modal === 'create' ? 'Create User' : 'Edit User'}</h3>
            <form onSubmit={handleSave}>
              <div className="sm-field">
                <label>Name *</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required autoFocus />
              </div>
              {modal === 'create' && (
                <>
                  <div className="sm-field">
                    <label>Email *</label>
                    <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
                  </div>
                  <div className="sm-field">
                    <label>Password *</label>
                    <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required placeholder="Min 8 chars, include special char" />
                  </div>
                </>
              )}
              <div className="sm-field">
                <label>Role</label>
                <select className="sm-select" style={{ width: '100%' }} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              {modal !== 'create' && (
                <div className="sm-field">
                  <label>Status</label>
                  <select className="sm-select" style={{ width: '100%' }} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
              {error && <p className="sm-err"><span>⚠</span> {error}</p>}
              <div className="sm-modal-footer">
                <button type="button" className="sm-btn sm-btn-ghost" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="sm-btn sm-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : modal === 'create' ? 'Create' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
